import { useEffect, useState } from 'react';
import useFilterStore from '../../../components/streetview/filterStore';
import PostStreetviewHouseholdDetail from '../../../services/adapters/streetviewHouseholdDetail';
import { BenefitsBadgeVariantValue } from '../../../shared/components';
import { DynamicFilters } from '../../../types/enums';
import { SelectedFilters } from '../../../types/filters.type';
import { Household } from '../../../types/global';
import useSnapshot from '../../../utils/snapshot';
import HouseholdCard from '../components/householdCard';
import { HouseholdCardDetail, HouseholdCardStatusValues } from '../streetview.type';
import { getHouseholdCardItems } from '../utils';
import TakeActionMenu from './takeActionMenu';

// The household reference search sits in the filter store under an empty category
const referenceCategory = '';
const referenceFilterName = 'Search by Housing Benefit Reference';

export type HouseholdCardContainerProps = {
  household: Household,
  benefitsVariant: BenefitsBadgeVariantValue,
  onClose: () => void,
}

// Household card with the Take Action menu. Loads the household's details itself
// Picking an action limits the Street View filters to this household
export default function HouseholdCardContainer({
  household, benefitsVariant, onClose,
}: Readonly<HouseholdCardContainerProps>) {
  const { clientName, householdReference } = household;
  const { snapshot } = useSnapshot();
  const [detail, setDetail] = useState<HouseholdCardDetail>({ status: HouseholdCardStatusValues.Loading });
  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const setSelectedFilters = useFilterStore((state) => state.setSelectedFilters);

  useEffect(() => {
    const abortController = new AbortController();
    const loadHouseholdDetail = async () => {
      try {
        const loaded = await new PostStreetviewHouseholdDetail().adaptResponse({
          clientName,
          snapshot: snapshot.rawSnapshot,
          householdReference,
        }, abortController.signal);
        if (!abortController.signal.aborted) {
          setDetail({ status: HouseholdCardStatusValues.Loaded, items: getHouseholdCardItems(loaded) });
        }
      } catch {
        if (abortController.signal.aborted) return;
        setDetail({
          status: HouseholdCardStatusValues.Error,
          message: `Unable to load details for ${householdReference}. Please try again.`,
        });
      }
    };

    void loadHouseholdDetail();
    return () => abortController.abort();
  }, [clientName, householdReference, snapshot.rawSnapshot]);

  const reference = { id: householdReference, displayName: householdReference };
  const referenceFilter = { displayName: referenceFilterName, selectedValues: [reference] };
  const householdFilters: SelectedFilters = {
    ...selectedFilters,
    [referenceCategory]: { ...selectedFilters[referenceCategory], [DynamicFilters.HouseholdReferences]: referenceFilter },
  };

  const limitFiltersToHousehold = () => setSelectedFilters(
    referenceCategory,
    DynamicFilters.HouseholdReferences,
    referenceFilterName,
    [reference],
  );

  return (
    <HouseholdCard
      householdReference={householdReference}
      benefitsVariant={benefitsVariant}
      detail={detail}
      onClose={onClose}
      actionMenu={(
        <TakeActionMenu
          label="Take Action"
          filters={householdFilters}
          onAction={limitFiltersToHousehold}
          shouldLockCampaignFilters
        />
      )}
    />
  );
}
